import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, Loader2, Search, Check, ShoppingBag, LinkIcon, Copy, Trash2, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";

export default function Domain() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [tab, setTab] = useState("buy");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkDomain, setLinkDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}`);
      setSite(data.site);
      setQ(data.site.business_name || "");
    } catch { navigate("/dashboard"); }
  }, [siteId, navigate]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!q.trim() || tab !== "buy") return;
    const t = setTimeout(() => search(q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, tab]);

  const search = async (query) => {
    setSearching(true);
    try {
      const { data } = await api.post("/domains/search", { query });
      setResults(data.suggestions || []);
    } catch { /* silent */ } finally { setSearching(false); }
  };

  const buy = async (domain) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/domain/buy`, { domain });
      setSite(data.site);
      toast.success(`${domain} purchased (demo)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not buy"); }
    finally { setBusy(false); }
  };

  const link = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/domain/link`, { domain: linkDomain });
      setSite(data.site);
      toast.success("Domain linked. Add the DNS records to activate.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the custom domain from this site?")) return;
    try {
      const { data } = await api.delete(`/sites/${siteId}/domain`);
      setSite(data.site);
      toast.success("Domain removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!site) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C9A24B] wt-spin" /></div>;

  const current = site.domain;

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(`/sites/${siteId}`)} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>

        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <Globe className="w-7 h-7 text-[#C9A24B]" /> Custom Domain
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Buy a cheap domain in a tap, or connect one you already own.</p>
        </div>

        {current && (
          <div data-testid="domain-current-card" className="mb-6 rounded-2xl border border-[#C9A24B]/30 bg-[#C9A24B]/5 p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Currently attached</div>
              <div className="font-display text-xl font-bold text-[#E5C378] mt-1">{current.domain}</div>
              <div className="text-xs text-zinc-400 mt-1">
                {current.source === "bought" ? "Purchased through Webtu" : "Linked from your registrar"} · status: <b className={current.status === "active" ? "text-[#22C55E]" : "text-[#F59E0B]"}>{current.status.replaceAll("_", " ")}</b>
              </div>
            </div>
            <button data-testid="domain-remove-btn" onClick={remove}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm hover:border-[#EF4444]/50 hover:text-[#EF4444]">
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex rounded-2xl border border-[#27272A] p-1 bg-[#161616] mb-6">
          <button data-testid="domain-tab-buy" onClick={() => setTab("buy")}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all inline-flex items-center gap-2 ${tab === "buy" ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-300"}`}>
            <ShoppingBag className="w-4 h-4" /> Buy a domain
          </button>
          <button data-testid="domain-tab-link" onClick={() => setTab("link")}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all inline-flex items-center gap-2 ${tab === "link" ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-300"}`}>
            <LinkIcon className="w-4 h-4" /> Connect your own
          </button>
        </div>

        {tab === "buy" ? (
          <div>
            <div className="relative mb-5">
              <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input data-testid="domain-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search your shop name — e.g. sunrise salon"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-[#161616] border border-[#27272A] text-zinc-100 focus:outline-none focus:border-[#C9A24B]" />
            </div>
            {searching ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#C9A24B] wt-spin" /></div>
            ) : results.length ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {results.map((r) => (
                  <div key={r.domain} data-testid={`domain-result-${r.domain}`}
                    className={`rounded-2xl border p-4 flex items-center justify-between gap-3 ${r.recommended ? "border-[#C9A24B]/50 bg-[#C9A24B]/5" : "border-[#27272A] bg-[#161616]"}`}>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.domain}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        ₹{r.price}/year
                        {r.recommended && <span className="ml-2 text-[10px] font-bold text-[#E5C378] bg-[#C9A24B]/15 border border-[#C9A24B]/30 rounded-full px-2 py-0.5">RECOMMENDED</span>}
                        {r.popular && !r.recommended && <span className="ml-2 text-[10px] text-zinc-500">Popular</span>}
                      </div>
                    </div>
                    <button data-testid={`domain-buy-${r.domain}`} onClick={() => buy(r.domain)} disabled={busy || current?.domain === r.domain}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] text-sm font-bold active:scale-[0.98] disabled:opacity-50 shrink-0">
                      {current?.domain === r.domain ? <Check className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
                      {current?.domain === r.domain ? "Yours" : "Buy"}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-[#27272A] p-10 text-center text-zinc-400">
                <Sparkles className="w-6 h-6 text-[#C9A24B] mx-auto mb-2" />
                Type your business name to see cheap available domains.
              </div>
            )}
            <p className="text-[11px] text-zinc-500 mt-4 text-center">
              Prices are shown for demo. Real domains go live when your reseller account is connected.
            </p>
          </div>
        ) : (
          <div>
            <div className="rounded-2xl border border-[#27272A] bg-[#161616] p-5">
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Your domain</label>
              <input data-testid="domain-link-input" value={linkDomain} onChange={(e) => setLinkDomain(e.target.value)}
                placeholder="myshop.in"
                className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 text-sm text-zinc-100 focus:outline-none focus:border-[#C9A24B]" />
              <button data-testid="domain-link-submit" onClick={link} disabled={busy || !linkDomain.trim()}
                className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold px-5 py-2.5 rounded-xl disabled:opacity-50">
                <LinkIcon className="w-4 h-4" /> Link Domain
              </button>
            </div>
            {current?.source === "linked" && current.dns_records && (
              <div data-testid="domain-dns-card" className="mt-5 rounded-2xl border border-[#27272A] bg-[#161616] p-5">
                <h3 className="font-display font-bold mb-1">Add these DNS records at your registrar</h3>
                <p className="text-xs text-zinc-500 mb-4">Login to GoDaddy / Namecheap / wherever you bought <b>{current.domain}</b>, open DNS, and add:</p>
                <div className="space-y-2">
                  {current.dns_records.map((r, i) => (
                    <div key={i} data-testid={`domain-dns-row-${i}`} className="grid grid-cols-4 gap-3 text-sm bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                      <div><span className="text-[10px] text-zinc-500 uppercase">Type</span><div className="text-[#E5C378] font-mono">{r.type}</div></div>
                      <div><span className="text-[10px] text-zinc-500 uppercase">Host</span><div className="font-mono">{r.host}</div></div>
                      <div className="col-span-2 min-w-0 flex items-center gap-2"><div><span className="text-[10px] text-zinc-500 uppercase">Value</span><div className="font-mono truncate">{r.value}</div></div>
                        <button data-testid={`domain-dns-copy-${i}`} onClick={async () => {
                          try { await navigator.clipboard.writeText(r.value); toast.success("Copied"); }
                          catch { toast.error("Copy failed — long-press to copy"); }
                        }} className="ml-auto text-zinc-400 hover:text-[#C9A24B]"><Copy className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-3">DNS changes take 5 minutes to 24 hours. We'll auto-verify.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
